import {
  Anchor,
  Box,
  Notification,
  NotificationProps,
  ThemeProps,
} from "@deliverr/ui";
import styled from "@emotion/styled";
import React, { FC } from "react";
import {
  Navigate,
  useRouteError,
  isRouteErrorResponse,
} from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { maxWidthCenterNarrowStyle } from "../common/styleUtils";
import { MailToFreight } from "../common/linkToWebsite";

const Container = styled(Box)<ThemeProps>(
  ({ theme }) => `
    background-color: ${theme.colors.NEUTRAL["00"]};
    padding: ${theme.spacing.S7} 0;

    > * {
      ${maxWidthCenterNarrowStyle}
      padding: ${theme.spacing.S5};
    }
  `
);

const serviceErrorContent: NotificationProps["content"] = {
  title: (
    <FormattedMessage
      id="trackingError.serviceError.title"
      defaultMessage="We couldn't load your tracking details"
    />
  ),
  description: (
    <FormattedMessage
      id="trackingError.serviceError.description"
      defaultMessage="Something went wrong on our end. Please try again in a few minutes. If the issue persists, reach out to {email} or your account representative."
      values={{
        email: <Anchor href={MailToFreight}>freight@deliverr.com</Anchor>,
      }}
    />
  ),
};

export const TrackingError: FC = () => {
  const error = useRouteError();

  // A 404 means the BOL genuinely does not exist — send the user back to the
  // input screen with the existing "not found" messaging.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <Navigate to="/" state={{ error: true }} />;
  }

  // Any other failure (5xx, network) is a service problem, not a bad BOL.
  // Surfacing it as "not found" would wrongly tell users their shipment
  // does not exist, so show a distinct, retryable message instead.
  return (
    <Container>
      <Notification appearance="DANGER" content={serviceErrorContent} />
    </Container>
  );
};
